import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Snackbar, Alert, CircularProgress } from '@mui/material'
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

/**
 * Floating toast that reflects the current state of the background sync engine.
 *
 * Styled to match the app's existing toast pattern (Snackbar + Alert) and the
 * Figma spec (Rediseño Lobby, node 1746-15966): white background, medium drop
 * shadow, Nunito Sans SemiBold 14 px, top-right anchor.
 *
 * Lifecycle driven by three window events dispatched by SyncEngine.js:
 *  - `lobby:sync-start`    → shows spinner + "Sincronizando…" (stays until resolved)
 *  - `lobby:sync-complete` → switches to success for AUTO_HIDE_MS, then hides
 *  - `lobby:sync-error`    → switches to error for ERROR_HIDE_MS, then hides
 *
 * `phaseRef` is updated synchronously in the event handlers so a `complete`
 * that arrives in the same tick as `start` (a fast empty-queue `runSync`)
 * is not ignored while React still has `phase === 'idle'` queued.
 */

const AUTO_HIDE_MS = 3000
const ERROR_HIDE_MS = 6000
const STUCK_SYNC_MS = 60_000

/** @typedef {'idle'|'syncing'|'done'|'error'} SyncPhase */

export default function SyncIndicator() {
  const { t } = useTranslation()

  /** @type {[SyncPhase, React.Dispatch<SyncPhase>]} */
  const [phase, setPhase] = useState('idle')
  /** @type {React.MutableRefObject<SyncPhase>} */
  const phaseRef = useRef('idle')
  const hideTimerRef = useRef(null)

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const setPhaseSync = useCallback((next) => {
    phaseRef.current = next
    setPhase(next)
  }, [])

  const handleStart = useCallback(() => {
    clearHideTimer()
    setPhaseSync('syncing')
  }, [clearHideTimer, setPhaseSync])

  const handleComplete = useCallback(() => {
    // `runPoll` fires `lobby:sync-complete` without `lobby:sync-start`.
    // Showing "Sincronizado" from idle made every background poll look like the
    // pending offline visit had uploaded.
    if (phaseRef.current !== 'syncing') return
    setPhaseSync('done')
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      if (phaseRef.current === 'done') setPhaseSync('idle')
    }, AUTO_HIDE_MS)
  }, [clearHideTimer, setPhaseSync])

  const handleError = useCallback(() => {
    setPhaseSync('error')
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      if (phaseRef.current === 'error') setPhaseSync('idle')
    }, ERROR_HIDE_MS)
  }, [clearHideTimer, setPhaseSync])

  useEffect(() => {
    window.addEventListener('lobby:sync-start',    handleStart)
    window.addEventListener('lobby:sync-complete', handleComplete)
    window.addEventListener('lobby:sync-error',    handleError)
    return () => {
      window.removeEventListener('lobby:sync-start',    handleStart)
      window.removeEventListener('lobby:sync-complete', handleComplete)
      window.removeEventListener('lobby:sync-error',    handleError)
    }
  }, [handleStart, handleComplete, handleError])

  // UI-only watchdog: if "Sincronizando…" never gets complete/error, hide it
  // so a kiosk doesn't stay blocked until CTRL+R. Does NOT touch `isSyncing`.
  useEffect(() => {
    if (phase !== 'syncing') return undefined
    const timer = setTimeout(() => {
      if (phaseRef.current !== 'syncing') return
      setPhaseSync('error')
      hideTimerRef.current = setTimeout(() => {
        if (phaseRef.current === 'error') setPhaseSync('idle')
      }, ERROR_HIDE_MS)
    }, STUCK_SYNC_MS)
    return () => clearTimeout(timer)
  }, [phase, setPhaseSync])

  useEffect(() => () => clearHideTimer(), [clearHideTimer])

  const phaseConfig = {
    syncing: {
      severity: 'info',
      icon:     <CircularProgress size={18} thickness={5} color="inherit" />,
      label:    t('sync_in_progress'),
      autoHideDuration: null,
      showClose: false,
    },
    done: {
      severity: 'success',
      icon:     <CheckCircleOutline fontSize="small" />,
      label:    t('sync_done'),
      autoHideDuration: AUTO_HIDE_MS,
      showClose: true,
    },
    error: {
      severity: 'error',
      icon:     <ErrorOutline fontSize="small" />,
      label:    t('sync_failed'),
      autoHideDuration: ERROR_HIDE_MS,
      showClose: true,
    },
    idle: null,
  }

  const config = phaseConfig[phase]

  const handleClose = (_event, reason) => {
    if (reason === 'clickaway') return
    clearHideTimer()
    setPhaseSync('idle')
  }

  return (
    <Snackbar
      open={phase !== 'idle'}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      autoHideDuration={config?.autoHideDuration ?? null}
      onClose={handleClose}
      sx={{ top: { xs: 72, sm: 80 } }}
    >
      {config ? (
        <Alert
          severity={config.severity}
          icon={config.icon}
          onClose={config.showClose ? handleClose : undefined}
          sx={{
            boxShadow: '0px 4px 6px -1px rgba(16,24,40,0.10), 0px 2px 4px -2px rgba(16,24,40,0.10)',
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 600,
            fontSize: 14,
            alignItems: 'center',
            pr: config.showClose ? 1 : 2,
          }}
        >
          {config.label}
        </Alert>
      ) : <span />}
    </Snackbar>
  )
}
